/**
 * 4 Nov 2026 (D-1104-NEEDS) — "Still needs a date" on Planning: Book and Choose another day on each row, tenants not
 * seen last month first, a No Show or Unable to Complete this month back on the list, the tenant's usual day as the best
 * first day, and the 15th-of-the-month line under Needs your attention. One Admin → Settings switch for each, all On
 * unless the row says No (a site with no row has them on). With a switch Off the screen is exactly what it was.
 *
 *   HK_NEEDS_BOOK            Book <day> · Choose another day · Pencil in <day>      Off: the three "Put the visit here" buttons
 *   HK_NOT_SEEN_FIRST        tenants not seen last month first, with a label        Off: fewest options first, no label
 *   HK_MISSED_BACK_ON_LIST   a No Show / Unable to Complete is not a held visit     Off: today's count
 *   HK_USUAL_DAY_FIRST       the tenant's usual weekday is the best first day       Off: the lightest day
 *   HK_SECOND_VISIT_NUDGE    from the 15th: "N tenants have only 1 of 2 visits"      Off: no line
 *
 * Pure: the API and the page share these. Nothing here writes.
 */
import { dayOfWeek } from './dates';

export const NEEDS_BOOK_KEY = 'HK_NEEDS_BOOK';
export const NOT_SEEN_FIRST_KEY = 'HK_NOT_SEEN_FIRST';
export const MISSED_BACK_KEY = 'HK_MISSED_BACK_ON_LIST';
export const USUAL_DAY_FIRST_KEY = 'HK_USUAL_DAY_FIRST';
export const SECOND_VISIT_NUDGE_KEY = 'HK_SECOND_VISIT_NUDGE';

export const NEEDS_SWITCH_KEYS = [NEEDS_BOOK_KEY, NOT_SEEN_FIRST_KEY, MISSED_BACK_KEY, USUAL_DAY_FIRST_KEY, SECOND_VISIT_NUDGE_KEY] as const;

type Settings = Record<string, string | undefined> | undefined | null;

/** On unless the row says No (a site with no row has it on). */
export function needsSwitchOn(settings: Settings, key: (typeof NEEDS_SWITCH_KEYS)[number]): boolean {
  return !/^(no|false|off|0)$/i.test(String(settings?.[key] ?? '').trim());
}

/** The first day of the month the second-visit line starts: the 15th. */
export const NUDGE_FROM_DAY = 15;

/** True from the 15th of the month, for a civil day key. */
export const nudgeDay = (todayKey: string): boolean => Number(todayKey.slice(6, 8)) >= NUDGE_FROM_DAY;

/**
 * The statuses of a visit that did not happen. The processor's own words for both: "Recorded as a no-show. The slot was
 * released and the visit does not count against the tenant's approved visits." / "Recorded as unable to complete. The
 * slot was released and the visit does not count against the tenant's approved visits." (processor/definition_q1_v8o.json,
 * the Record No Show / Record Unable To Complete outcome message).
 */
export const MISSED_STATUSES: readonly string[] = ['No Show', 'Unable to Complete'];
export const isMissedStatus = (status: string | null | undefined): boolean => MISSED_STATUSES.includes(String(status ?? ''));

/* ---------------------------------------------------------------------------------------------- */
/* Seen last month                                                                                   */
/* ---------------------------------------------------------------------------------------------- */

/**
 * The label a row on Still needs a date carries from last month:
 *   notSeen   no Completed visit last month (a No Show, Unable to Complete, Cancelled, an unanswered pencil, or nothing);
 *             `noRecord` is true when there is no visit of any kind on record for the month, in the app or the old system
 *   seenOne   a twice-a-month tenant with exactly 1 Completed visit
 * Anyone else is not marked (seen enough, or new this month).
 */
export interface LastMonth {
  state: 'notSeen' | 'seenOne';
  noRecord?: boolean;
}

export interface LastMonthInput {
  /** Visits authorized this month (null: no fixed limit). */
  authorized: number | null;
  /** The days they were seen last month: Completed in the app and "done" in the old system, once each. */
  seenDays: readonly string[];
  /** Any visit at all on record for last month, of any status, in the app or the old system. */
  anyRecord: boolean;
  /** Enrolled this month: a new tenant has no last month to be missed in. */
  enrolledThisMonth: boolean;
}

/** Null where the tenant is not marked. Staff units and offices never reach here (they are not on the worklist). */
export function lastMonthOf(i: LastMonthInput): LastMonth | null {
  if (i.enrolledThisMonth) return null;
  const seen = new Set(i.seenDays).size;
  if (seen === 0) return { state: 'notSeen', ...(i.anyRecord ? {} : { noRecord: true }) };
  if (i.authorized !== null && i.authorized >= 2 && seen === 1) return { state: 'seenOne' };
  return null;
}

/** The sort group on Still needs a date: Not seen, then Seen 1 of 2, then everyone else. */
export const lastMonthGroup = (m: LastMonth | null | undefined): 0 | 1 | 2 => (m?.state === 'notSeen' ? 0 : m?.state === 'seenOne' ? 1 : 2);

/* ---------------------------------------------------------------------------------------------- */
/* The usual day                                                                                     */
/* ---------------------------------------------------------------------------------------------- */

/** A tenant needs at least this many Completed visits for a usual day. */
export const USUAL_DAY_MIN_VISITS = 2;

/**
 * The weekday (1 Monday … 5 Friday) on which most of their Completed visits fell — the days given are the ones in the last
 * 3 months. Needs 2 visits or more. A tie goes to the weekday of the most recent of the tied visits. Null: no usual day.
 */
export function usualWeekday(completedDays: readonly string[]): number | null {
  const days = [...new Set(completedDays)].filter((k) => {
    const d = dayOfWeek(k);
    return d >= 1 && d <= 5;
  });
  if (days.length < USUAL_DAY_MIN_VISITS) return null;
  const count = new Map<number, { n: number; last: string }>();
  for (const k of days) {
    const d = dayOfWeek(k);
    const c = count.get(d) ?? { n: 0, last: '' };
    count.set(d, { n: c.n + 1, last: k > c.last ? k : c.last });
  }
  let best: [number, { n: number; last: string }] | null = null;
  for (const e of count) if (!best || e[1].n > best[1].n || (e[1].n === best[1].n && e[1].last > best[1].last)) best = e;
  return best ? best[0] : null;
}
